import { Button, Badge } from "@/ui";
import { Play } from "lucide-react";
import { useState, useEffect } from "react";

const heroImages = [
  "/assets/hero/Healthcare_AI_hero_image_82503f18.png",
  "/assets/shared/Healthcare_AI_platform_interface_d8311b6d.png",
  "/assets/shared/Patients_using_health_technology_ea50cdeb.png",
];

// Brand colors
const PRIMARY_BLUE = "#1356a6";
const HERO_BLUE = "#0d3b6e";

export default function Hero() {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  // Rotate images every 4 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prev) => (prev + 1) % heroImages.length);
    }, 4000);

    return () => clearInterval(interval);
  }, []);

  return (
    <section 
      className="relative isolate min-h-[48vh] flex items-start pt-16 pb-24 lg:pb-32 rounded-2xl overflow-hidden p-8 lg:p-12"
    >
      {/* Floating decorative elements */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div
          className="absolute top-20 left-10 w-64 h-64 rounded-full opacity-20 animate-float"
          style={{ background: `radial-gradient(circle, ${PRIMARY_BLUE}40 0%, transparent 70%)` }}
        />
        <div
          className="absolute bottom-20 right-20 w-96 h-96 rounded-full opacity-15 animate-float-delayed"
          style={{ background: `radial-gradient(circle, ${PRIMARY_BLUE}30 0%, transparent 70%)` }}
        />
        <div
          className="absolute top-1/2 left-1/3 w-48 h-48 rounded-full opacity-10 animate-float-slow"
          style={{ background: `radial-gradient(circle, white 0%, transparent 70%)` }}
        />
      </div>

      {/* Content */}
      <div className="relative z-10 w-full">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-stretch">
            {/* Left: content with staggered animations */}
            <div className="max-w-none">
              {/* Launch Date Badge */}
              <Badge className="mb-6 border-none animate-fade-in-up" style={{ backgroundColor: `${PRIMARY_BLUE}20`, color: PRIMARY_BLUE }}>
                Launching Q2 2026
              </Badge>
              
              {/* Headline with gradient text */}
              <h1
                className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-normal leading-tight mb-12 animate-fade-in-up"
                style={{ color: PRIMARY_BLUE, lineHeight: "1.2" }}
                data-testid="text-hero-title"
              >
                Urgent Care{" "}
                <br />
                Reimagined with{" "}
                <br />
                <span
                  className="bg-clip-text text-transparent"
                  style={{
                    backgroundImage: `linear-gradient(135deg, #60a5fa 0%, #3b82f6 50%, ${PRIMARY_BLUE} 100%)`,
                  }}
                >
                  AI
                </span>{" "}
                at the Core
              </h1>

              {/* Subheadline */}
              <p
                className="text-2xl sm:text-3xl mb-6 leading-relaxed animate-fade-in-up animation-delay-100"
                style={{ color: PRIMARY_BLUE }}
                data-testid="text-hero-subheadline"
              >
                Ground-up,{" "}
                <span
                  className="bg-clip-text text-transparent"
                  style={{
                    backgroundImage: `linear-gradient(135deg, #10b981 0%, #0891b2 50%, #0d9488 100%)`,
                  }}
                >
                  AI-native
                </span>{" "}
                Urgent Care EMR — not a legacy EMR with AI bolted on.
              </p>
              <p className="text-2xl sm:text-3xl mb-8 leading-relaxed animate-fade-in-up animation-delay-200" style={{ color: PRIMARY_BLUE }}>
                Purpose-built for{" "}
                <span
                  className="bg-clip-text text-transparent"
                  style={{
                    backgroundImage: `linear-gradient(135deg, #10b981 0%, #0891b2 50%, #0d9488 100%)`,
                  }}
                >
                  Urgent Care
                </span>{" "}
                and{" "}
                <span
                  className="bg-clip-text text-transparent"
                  style={{
                    backgroundImage: `linear-gradient(135deg, #10b981 0%, #0891b2 50%, #0d9488 100%)`,
                  }}
                >
                  Occupational Medical Service
                </span>{" "}
                — powered by{" "}
                <span
                  className="bg-clip-text text-transparent"
                  style={{
                    backgroundImage: `linear-gradient(135deg, #10b981 0%, #0891b2 50%, #0d9488 100%)`,
                  }}
                >
                  Agentic AI
                </span>{" "}
                that automates every workflow, end to end.
              </p>

              {/* CTAs with 3D effect */}
              <div className="flex flex-col sm:flex-row gap-4 animate-fade-in-up animation-delay-700">
                <Button
                  size="lg"
                  className="w-full sm:w-auto gap-2 font-bold rounded-full transition-all duration-200 hover:-translate-y-1 active:translate-y-0"
                  style={{
                    backgroundColor: HERO_BLUE,
                    color: "white",
                    boxShadow: "0 4px 0 #0a2d4d, 0 6px 20px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.1)",
                    border: "1px solid rgba(255,255,255,0.2)",
                  }}
                  onClick={() => {
                    const element = document.getElementById('platform-features');
                    element?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }}
                  data-testid="hero-cta-secondary"
                >
                  <Play className="w-5 h-5" style={{ color: "#0AB89E" }} />
                  Explore Features
                </Button>
              </div>
            </div>

            {/* Right: rotating hero images */}
            <div className="flex items-stretch justify-center animate-fade-in-up animation-delay-400">
              <div className="w-full group">
                <div className="relative h-full">
                  {/* Glow effect behind images */}
                  <div
                    className="absolute inset-0 rounded-2xl blur-2xl opacity-30 group-hover:opacity-50 transition-opacity duration-500"
                    style={{ backgroundColor: PRIMARY_BLUE }}
                  />
                  {/* Rotating images */}
                  {heroImages.map((image, idx) => (
                    <img
                      key={idx}
                      src={image}
                      alt={`Hero image ${idx + 1}`}
                      className={`absolute inset-0 w-full h-full rounded-2xl shadow-2xl border border-white/20 transition-all duration-1000 ease-in-out group-hover:scale-[1.02] ${
                        idx === currentImageIndex
                          ? "opacity-100 scale-100 z-10"
                          : "opacity-0 scale-95 z-0"
                      }`}
                      style={{ 
                        objectFit: "contain",
                      }}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
      </div>
    </section>
  );
}
